import type { ComponentType, SVGProps } from 'react';
import {
  Calendar,
  ChatBubble,
  ClipboardCheck,
  Code,
  Folder,
  GraphUp,
  Label,
  Mail,
  Megaphone,
  Page,
} from 'iconoir-react';

// Sample apps from the design (Figma 10631:26332) until this page is wired to the integrations API.
export type IntegrationCategory =
  | 'files'
  | 'email-calendar'
  | 'chat'
  | 'sales'
  | 'projects'
  | 'code';

export const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  files: 'Files',
  'email-calendar': 'Email and calendar',
  chat: 'Chat',
  sales: 'Sales',
  projects: 'Projects',
  code: 'Code',
};

export type IntegrationApp = {
  id: string;
  name: string;
  description: string;
  category: IntegrationCategory;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  connection?: {
    needsReconnect?: boolean;
    access: string;
    audience: string;
    agentCount: number;
  };
};

export const INTEGRATION_APPS: IntegrationApp[] = [
  {
    id: 'github',
    name: 'GitHub',
    description: "Agents can't use this until you reconnect",
    category: 'code',
    icon: Code,
    connection: { needsReconnect: true, access: 'Read only', audience: 'Everyone', agentCount: 2 },
  },
  {
    id: 'sharepoint',
    name: 'SharePoint',
    description: 'Find, read and update company files',
    category: 'files',
    icon: Folder,
    connection: { access: 'Read and change', audience: 'Everyone', agentCount: 2 },
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Work with contacts, companies and deals',
    category: 'sales',
    icon: Megaphone,
    connection: { access: 'Read and change', audience: '3 people', agentCount: 2 },
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    description: 'Find and read documents, sheets and slides',
    category: 'files',
    icon: Page,
  },
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Search, read and send email',
    category: 'email-calendar',
    icon: Mail,
  },
  {
    id: 'outlook',
    name: 'Outlook',
    description: 'Search, read and send email',
    category: 'email-calendar',
    icon: Mail,
  },
  {
    id: 'calendar',
    name: 'Calendar',
    description: 'See availability and schedule meetings',
    category: 'email-calendar',
    icon: Calendar,
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Search channels and post messages',
    category: 'chat',
    icon: ChatBubble,
  },
  {
    id: 'microsoft-teams',
    name: 'Microsoft Teams',
    description: 'Search chats and post messages',
    category: 'chat',
    icon: ChatBubble,
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Work with leads, accounts and opportunities',
    category: 'sales',
    icon: GraphUp,
  },
  {
    id: 'notion',
    name: 'Notion',
    description: 'Search and update pages and databases',
    category: 'projects',
    icon: ClipboardCheck,
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Track and update issues',
    category: 'projects',
    icon: Label,
  },
];
